import React, { useEffect, useState } from "react";

import { useEquipmentPage } from "../context/EquipmentPageContext";

import {
  FiSearch,
  FiEye,
  FiMoreVertical,
  FiChevronLeft,
  FiChevronRight,
  FiWifi,
  FiWifiOff,
  FiBox,
  FiThermometer,
  FiCoffee,
  FiZap,
  FiDroplet,
  FiTrash2,
  FiHome,
  FiTool,
} from "react-icons/fi";

import { HiOutlineQueueList } from "react-icons/hi2";



// ============================================================
// CONSTANTS
// ============================================================

const PAGE_LIMIT = 8;

// ============================================================
// DATE FORMATTER
// ============================================================

const formatDate = (dateValue) => {
  if (!dateValue) {
    return "—";
  }

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ============================================================
// DAYS DIFFERENCE
// ============================================================

const getDaysDifference = (dateValue) => {
  if (!dateValue) {
    return null;
  }

  const targetDate = new Date(dateValue);

  if (Number.isNaN(targetDate.getTime())) {
    return null;
  }

  const today = new Date();

  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  const difference = targetDate.getTime() - today.getTime();

  return Math.ceil(difference / (1000 * 60 * 60 * 24));
};

// ============================================================
// RELATIVE DATE TEXT
// ============================================================

const getRelativeDateText = (dateValue) => {
  const days = getDaysDifference(dateValue);

  if (days === null) {
    return "—";
  }

  if (days === 0) {
    return "Today";
  }

  if (days === 1) {
    return "Tomorrow";
  }

  if (days > 1) {
    return `In ${days} days`;
  }

  if (days === -1) {
    return "1 day overdue";
  }

  return `${Math.abs(days)} days overdue`;
};

// ============================================================
// CATEGORY STYLE
// ============================================================

const getCategoryStyle = (category = "") => {
  const normalized = category.toLowerCase();

  if (normalized.includes("refriger")) {
    return {
      color: "bg-blue-600",
      icon: FiThermometer,
    };
  }

  if (
    normalized.includes("cook") ||
    normalized.includes("oven") ||
    normalized.includes("fryer")
  ) {
    return {
      color: "bg-indigo-600",
      icon: FiCoffee,
    };
  }

  if (normalized.includes("clean")) {
    return {
      color: "bg-cyan-600",
      icon: FiTool,
    };
  }

  if (normalized.includes("electric")) {
    return {
      color: "bg-amber-500",
      icon: FiZap,
    };
  }

  if (normalized.includes("water") || normalized.includes("utility")) {
    return {
      color: "bg-sky-500",
      icon: FiDroplet,
    };
  }

  if (normalized.includes("waste")) {
    return {
      color: "bg-slate-700",
      icon: FiTrash2,
    };
  }

  if (normalized.includes("sanitation")) {
    return {
      color: "bg-teal-600",
      icon: FiHome,
    };
  }

  return {
    color: "bg-slate-500",
    icon: FiBox,
  };
};

// ============================================================
// OPERATIONAL STATUS STYLE
// ============================================================

const getStatusStyle = (operationalStatus, decommissioned) => {
  if (decommissioned) {
    return {
      label: "Decommissioned",
      className: "bg-slate-100 text-slate-600 border-slate-200",
    };
  }

  switch (operationalStatus) {
    case "In Service":
      return {
        label: "In Service",
        className: "bg-green-50 text-green-700 border-green-200",
      };

    case "Under Maintenance":
      return {
        label: "Under Maintenance",
        className: "bg-amber-50 text-amber-700 border-amber-200",
      };

    case "Out of Service":
      return {
        label: "Out of Service",
        className: "bg-red-50 text-red-700 border-red-200",
      };

    case "Decommissioned":
      return {
        label: "Decommissioned",
        className: "bg-slate-100 text-slate-600 border-slate-200",
      };

    default:
      return {
        label: operationalStatus || "Unknown",
        className: "bg-slate-50 text-slate-600 border-slate-200",
      };
  }
};

// ============================================================
// PAT INFORMATION
// ============================================================

const getPatInfo = (equipment) => {
  if (!equipment.requiresPat) {
    return {
      status: "N/A",
      date: "Not Required",
      color: "text-gray-400",
    };
  }

  const days = getDaysDifference(equipment.nextPatDue);

  if (days !== null && days < 0) {
    return {
      status: "Due",
      date: "Overdue",
      color: "text-red-600 font-semibold",
    };
  }

  if (equipment.lastTestResult === "Fail") {
    return {
      status: "Fail",
      date: equipment.nextPatDue
        ? formatDate(equipment.nextPatDue)
        : "Action Required",

      color: "text-red-600 font-semibold",
    };
  }

  if (equipment.lastTestResult === "Pass") {
    return {
      status: "Pass",
      date: equipment.nextPatDue
        ? `Due ${formatDate(equipment.nextPatDue)}`
        : "No due date",

      color: "text-green-700",
    };
  }

  return {
    status: equipment.lastTestResult || "Pending",

    date: equipment.nextPatDue
      ? `Due ${formatDate(equipment.nextPatDue)}`
      : "No test date",

    color: "text-amber-600",
  };
};

// ============================================================
// SENSOR INFORMATION
// ============================================================

const getSensorInfo = (equipment) => {
  if (!equipment.sensorInstalled) {
    return {
      reference: "—",
      status: "No Sensor",
      color: "text-gray-400",
    };
  }

  const status = equipment.connectionStatus || "Unknown";

  let color = "text-gray-500";

  if (status === "Live") {
    color = "text-emerald-600";
  }

  if (status === "Offline" || status === "Disconnected") {
    color = "text-red-500";
  }

  return {
    reference: equipment.sensorId || equipment.sensorType || "Sensor",

    status,
    color,
  };
};

// ============================================================
// WARRANTY INFORMATION
// ============================================================

const getWarrantyInfo = (equipment) => {
  if (!equipment.warrantyActive) {
    return {
      status: "—",
      sub: "No Warranty",
      color: "text-gray-400",
    };
  }

  if (!equipment.warrantyExpiryDate) {
    return {
      status: "Unknown",
      sub: "No expiry date",
      color: "text-gray-700",
    };
  }

  const days = getDaysDifference(equipment.warrantyExpiryDate);

  if (days !== null && days < 0) {
    return {
      status: "Expired",
      sub: formatDate(equipment.warrantyExpiryDate),
      color: "text-red-600 font-semibold",
    };
  }

  if (days !== null && days <= 30) {
    return {
      status: "Expiring",
      sub: formatDate(equipment.warrantyExpiryDate),
      color: "text-amber-600 font-semibold",
    };
  }

  return {
    status: "Valid",

    sub: `until ${formatDate(equipment.warrantyExpiryDate)}`,

    color: "text-gray-700",
  };
};

// ============================================================
// MAINTENANCE INFORMATION
// ============================================================

const getMaintenanceInfo = (equipment) => {
  if (!equipment.requiresRoutineMaintenance) {
    return {
      date: "—",
      sub: "Not Required",
      color: "text-gray-400",
    };
  }

  if (!equipment.nextMaintenanceDate) {
    return {
      date: "—",
      sub: "Not Scheduled",
      color: "text-gray-400",
    };
  }

  const days = getDaysDifference(equipment.nextMaintenanceDate);

  let color = "text-gray-400";

  if (days !== null) {
    if (days < 0) {
      color = "text-red-600 font-semibold";
    } else if (days === 0) {
      color = "text-green-600 font-semibold";
    } else if (days <= 7) {
      color = "text-amber-600";
    }
  }

  return {
    date: formatDate(equipment.nextMaintenanceDate),

    sub: getRelativeDateText(equipment.nextMaintenanceDate),

    color,
  };
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function EquipmentInventory() {
  const { equipment: allEquipment, filteredRecords, filters, setFilters, metricFilter, range, loading, error, setSelectedEquipment, setMetricFilter } = useEquipmentPage();
  const { search, category, location } = filters;
  const setSearch = search => setFilters(previous => ({ ...previous, search }));
  const setCategory = category => setFilters(previous => ({ ...previous, category }));
  const setLocation = location => setFilters(previous => ({ ...previous, location }));
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [filters, metricFilter, range]);
  const categoryOptions = [...new Set(allEquipment.map(item => item.category).filter(Boolean))].sort();
  const locationOptions = [...new Set(allEquipment.map(item => item.location).filter(Boolean))].sort();
  const pageCount = Math.ceil(filteredRecords.length / PAGE_LIMIT);
  const safePage = Math.min(page, pageCount || 1);
  const equipments = filteredRecords.slice((safePage - 1) * PAGE_LIMIT, safePage * PAGE_LIMIT);
  const pagination = { total: filteredRecords.length, totalPages: pageCount, page: safePage, limit: PAGE_LIMIT };

  // ============================================================
  // PAGINATION VALUES
  // ============================================================

  const total = pagination?.total || 0;

  const totalPages = pagination?.totalPages || 0;

  const currentPage = pagination?.page || page;

  const limit = pagination?.limit || PAGE_LIMIT;

  const startRecord = total === 0 ? 0 : (currentPage - 1) * limit + 1;

  const endRecord = Math.min(currentPage * limit, total);

  // ============================================================
  // PAGE NUMBERS
  // ============================================================

  const pageNumbers = Array.from(
    {
      length: totalPages,
    },
    (_, index) => index + 1,
  );

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div id="equipment-inventory" className="w-full bg-white border border-slate-200 rounded-xl shadow-sm font-sans text-slate-900 overflow-hidden">
      {/* ======================================================
          HEADER
      ====================================================== */}

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-5 border-b border-slate-100 bg-white">
        <h2 className="text-lg font-bold text-slate-900 self-start sm:self-center">
          Equipment Inventory
        </h2>

        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto justify-end">
          {/* ==================================================
              SEARCH
          ================================================== */}

          <div className="relative w-full sm:w-64">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search equipment"
              placeholder="Search equipment..."
              className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder-slate-400"
            />
          </div>

          {/* ==================================================
              CATEGORY
          ================================================== */}

          <select
            aria-label="Equipment category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-3 py-2 text-sm text-slate-700 bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 cursor-pointer min-w-[130px]"
          >
            <option value="">All Categories</option>

            {categoryOptions.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          {/* ==================================================
              LOCATION
          ================================================== */}

          <select
            aria-label="Equipment location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="px-3 py-2 text-sm text-slate-700 bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 cursor-pointer min-w-[130px]"
          >
            <option value="">All Locations</option>

            {locationOptions.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          {/* ==================================================
              LIST VIEW
          ================================================== */}

          <button
            type="button"
            className="p-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
            title="Reset inventory filters"
            onClick={() => { setFilters({ search: "", category: "", location: "", status: "", riskLevel: "" }); setMetricFilter(""); }}
          >
            <HiOutlineQueueList className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* ======================================================
          ERROR
      ====================================================== */}

      {error && (
        <div className="mx-5 mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm font-semibold text-red-700">
            Unable to load equipment
          </p>

          <p className="text-xs text-red-600 mt-0.5">{error}</p>
        </div>
      )}

      {/* ======================================================
          TABLE
      ====================================================== */}

      <div className="w-full overflow-x-auto">
        <table className="w-full text-left border-collapse min-w-[1000px]">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50 text-xs font-bold text-slate-500 uppercase tracking-wider">
              <th className="py-3 px-5">Equipment</th>

              <th className="py-3 px-4">Category</th>

              <th className="py-3 px-4">Location</th>

              <th className="py-3 px-4">Status</th>

              <th className="py-3 px-4">PAT Status</th>

              <th className="py-3 px-4">IoT Sensor</th>

              <th className="py-3 px-4">Warranty</th>

              <th className="py-3 px-4">Next Maintenance</th>

              <th className="py-3 px-4 text-center">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 text-sm">
            {/* ==================================================
                LOADING
            ================================================== */}

            {loading && (
              <tr>
                <td colSpan={9} className="py-12 text-center">
                  <div className="flex items-center justify-center gap-3">
                    <span className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />

                    <span className="text-sm font-medium text-slate-500">
                      Loading equipment...
                    </span>
                  </div>
                </td>
              </tr>
            )}

            {/* ==================================================
                EMPTY STATE
            ================================================== */}

            {!loading && equipments.length === 0 && (
              <tr>
                <td colSpan={9} className="py-14 text-center">
                  <FiBox className="w-8 h-8 text-slate-300 mx-auto mb-2" />

                  <p className="text-sm font-semibold text-slate-600">
                    No equipment found
                  </p>

                  <p className="text-xs text-slate-400 mt-1">
                    Add equipment or change your search filters.
                  </p>
                </td>
              </tr>
            )}

            {/* ==================================================
                EQUIPMENT ROWS
            ================================================== */}

            {!loading &&
              equipments.map((equipment) => {
                const categoryStyle = getCategoryStyle(equipment.category);

                const EquipmentIcon = categoryStyle.icon;

                const statusInfo = getStatusStyle(
                  equipment.operationalStatus,
                  equipment.decommissioned,
                );

                const patInfo = getPatInfo(equipment);

                const sensorInfo = getSensorInfo(equipment);

                const warrantyInfo = getWarrantyInfo(equipment);

                const maintenanceInfo = getMaintenanceInfo(equipment);

                return (
                  <tr
                    key={equipment.id}
                    className="hover:bg-slate-50/40 transition-colors"
                  >
                    {/* ========================================
                          EQUIPMENT
                      ======================================== */}

                    <td className="py-4 px-5">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-100/80 rounded-lg border border-slate-200/40 shrink-0">
                          <EquipmentIcon className="w-5 h-5 text-gray-500" />
                        </div>

                        <div className="flex flex-col">
                          <span className="font-semibold text-slate-800 leading-tight">
                            {equipment.name}
                          </span>

                          <span className="text-xs text-slate-400 mt-0.5 font-mono">
                            #{equipment.assetId}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* ========================================
                          CATEGORY
                      ======================================== */}

                    <td className="py-4 px-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${categoryStyle.color}`}
                        />

                        <span className="text-slate-700 font-medium">
                          {equipment.category || "—"}
                        </span>
                      </div>
                    </td>

                    {/* ========================================
                          LOCATION
                      ======================================== */}

                    <td className="py-4 px-4 text-slate-600 font-medium whitespace-nowrap">
                      <div className="flex flex-col">
                        <span>{equipment.location || "—"}</span>

                        {equipment.specificArea && (
                          <span className="text-[11px] text-slate-400 mt-0.5">
                            {equipment.specificArea}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* ========================================
                          STATUS
                      ======================================== */}

                    <td className="py-4 px-4 whitespace-nowrap">
                      <span
                        className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-md border ${statusInfo.className}`}
                      >
                        {statusInfo.label}
                      </span>
                    </td>

                    {/* ========================================
                          PAT STATUS
                      ======================================== */}

                    <td className="py-4 px-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span
                          className={`font-semibold text-xs ${patInfo.color}`}
                        >
                          {patInfo.status}
                        </span>

                        <span className="text-xs text-slate-400 mt-0.5">
                          {patInfo.date}
                        </span>
                      </div>
                    </td>

                    {/* ========================================
                          IOT SENSOR
                      ======================================== */}

                    <td className="py-4 px-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-slate-700 font-semibold text-xs flex items-center gap-1.5">
                          {sensorInfo.status === "Live" && (
                            <FiWifi className="w-3.5 h-3.5 text-emerald-500" />
                          )}

                          {(sensorInfo.status === "Offline" ||
                            sensorInfo.status === "Disconnected") && (
                            <FiWifiOff className="w-3.5 h-3.5 text-red-500" />
                          )}

                          {sensorInfo.reference}
                        </span>

                        <span
                          className={`text-xs ${sensorInfo.color} mt-0.5 font-medium`}
                        >
                          {sensorInfo.status}
                        </span>
                      </div>
                    </td>

                    {/* ========================================
                          WARRANTY
                      ======================================== */}

                    <td className="py-4 px-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className={`text-xs ${warrantyInfo.color}`}>
                          {warrantyInfo.status}
                        </span>

                        <span className="text-xs text-slate-400 mt-0.5">
                          {warrantyInfo.sub}
                        </span>
                      </div>
                    </td>

                    {/* ========================================
                          NEXT MAINTENANCE
                      ======================================== */}

                    <td className="py-4 px-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-slate-700 font-semibold text-xs">
                          {maintenanceInfo.date}
                        </span>

                        <span
                          className={`text-xs ${maintenanceInfo.color} mt-0.5`}
                        >
                          {maintenanceInfo.sub}
                        </span>
                      </div>
                    </td>

                    {/* ========================================
                          ACTIONS
                      ======================================== */}

                    <td className="py-4 px-4 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                          title="View details"
                          onClick={() => setSelectedEquipment(equipment)}
                        >
                          <FiEye className="w-4 h-4" />
                        </button>

                        <button
                          type="button"
                          className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
                          title="Equipment information and documents"
                          onClick={() => setSelectedEquipment(equipment)}
                        >
                          <FiMoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* ======================================================
          PAGINATION
      ====================================================== */}

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-slate-100 bg-white">
        <span className="text-xs font-medium text-slate-400">
          Showing{" "}
          <span className="font-semibold text-slate-700">{startRecord}</span> to{" "}
          <span className="font-semibold text-slate-700">{endRecord}</span> of{" "}
          <span className="font-semibold text-slate-700">{total}</span>{" "}
          equipment
        </span>

        {totalPages > 0 && (
          <div className="flex items-center gap-1">
            {/* ==================================================
                PREVIOUS
            ================================================== */}

            <button
              type="button"
              disabled={currentPage <= 1 || loading}
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              className="
                p-1.5
                rounded-lg
                border
                border-slate-200
                text-slate-600
                hover:bg-slate-50
                transition-colors
                disabled:text-slate-300
                disabled:bg-slate-50
                disabled:cursor-not-allowed
              "
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>

            {/* ==================================================
                PAGE NUMBERS
            ================================================== */}

            {pageNumbers.map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                disabled={loading}
                onClick={() => setPage(pageNumber)}
                className={`
                    w-8
                    h-8
                    flex
                    items-center
                    justify-center
                    text-sm
                    rounded-lg
                    transition-colors

                    ${
                      currentPage === pageNumber
                        ? `
                          bg-blue-600
                          text-white
                          font-semibold
                        `
                        : `
                          text-slate-600
                          hover:bg-slate-50
                          font-medium
                        `
                    }
                  `}
              >
                {pageNumber}
              </button>
            ))}

            {/* ==================================================
                NEXT
            ================================================== */}

            <button
              type="button"
              disabled={currentPage >= totalPages || loading}
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              className="
                p-1.5
                rounded-lg
                border
                border-slate-200
                text-slate-600
                hover:bg-slate-50
                transition-colors
                disabled:text-slate-300
                disabled:bg-slate-50
                disabled:cursor-not-allowed
              "
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
